import React, { useState } from 'react';
import { 
  Box, 
  Tooltip, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent,
  Typography,
  Avatar,
  useTheme,
  useMediaQuery,
  Divider
} from '@mui/material';
import { AccountTree as AccountTreeIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useGetUserTrustchainQuery } from '../../redux/thunks/users/usertrustchainsget';

const TrustChain = ({ userId }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Fetch trust chain data using the RTK Query hook
  const { 
    data: responseData, 
    isLoading, 
    isError 
  } = useGetUserTrustchainQuery(userId);

  // Extract trustChain array from the response object
  const trustChain = responseData?.trustChain || [];
  const { connected, sourceUser, targetUser } = responseData || {};

  // Don't render anything if there's an error, still loading, not connected, or no valid chain
  if (isError || !connected || trustChain.length <= 1) return null;

  // Show a spinner while loading
  if (isLoading) {
    return (
      <Box sx={{ display: 'inline-flex', ml: 1, alignItems: 'center' }}>
        <div className="spinner-grow spinner-grow-sm" role="status" style={{ width: '0.8rem', height: '0.8rem' }}>
          <span className="visually-hidden">Loading...</span>
        </div>
      </Box>
    );
  }

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Helper to get initials from displayName
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('');
  };

  // Format trust score to remove decimal if the value is a whole number
  const formatTrustScore = (score) => {
    if (score === undefined || score === null) return '0';
    
    // Convert to percentage (0-100 scale)
    const scaledScore = Math.round(score);
    
    return scaledScore.toString();
  };

  return (
    <>
      <Tooltip title="View Trust Connection">
        <IconButton 
          size="small" 
          onClick={(e) => {
            e.stopPropagation();
            handleOpen();
          }} 
          sx={{ 
            ml: 1,
            color: 'primary.main',
            '&:hover': {
              background: 'rgba(25, 118, 210, 0.04)'
            }
          }}
        >
          <AccountTreeIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Trust Chain Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1
          }
        }}
      >
        <DialogTitle>
          Trust Connection
          <Typography variant="body2" color="text.secondary">
            Your trust path to {targetUser?.displayName || trustChain[trustChain.length - 1]?.displayName}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              py: 2,
              overflowX: isMobile ? 'visible' : 'auto'
            }}
          >
            {trustChain.map((user, index) => {
              const isFirst = index === 0;
              const isLast = index === trustChain.length - 1;
              const opacity = isFirst || isLast ? 1 : 1 - (index * 0.1);
              
              return (
                <React.Fragment key={user.id.toString()}>
                  {/* User Node */}
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      width: isMobile ? '100%' : 'auto',
                      mb: isMobile ? 3 : 0
                    }}
                  >
                    {/* User Avatar */}
                    <Tooltip title={`Trust Score: ${formatTrustScore(user.trustScore)}`}>
                      <Avatar
                        src={user.profileImageUrl}
                        alt={user.displayName}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/profile/${user.id}/active`);
                        }}
                        sx={{ 
                          width: isFirst || isLast ? 64 : 48, 
                          height: isFirst || isLast ? 64 : 48,
                          opacity,
                          position: 'relative',
                          border: '3px solid',
                          borderColor: 'primary.main',
                          background: theme => `linear-gradient(45deg, ${theme.palette.primary.main} ${user.trustScore}%, ${theme.palette.grey[300]} 0%)`,
                          cursor: 'pointer',
                          '&:hover': {
                            boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.5)'
                          }
                        }}
                      >
                        {getInitials(user.displayName)}
                      </Avatar>
                    </Tooltip>
                    
                    {/* User Name and Status */}
                    <Typography 
                      variant={isFirst || isLast ? "subtitle1" : "body2"} 
                      align="center"
                      sx={{ mt: 1, fontWeight: isFirst || isLast ? 'bold' : 'normal' }}
                    >
                      {user.displayName}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      align="center"
                    >
                      {isFirst ? 'You' : isLast ? 'Viewing' : ''}
                    </Typography>
                  </Box>
                  
                  {/* Connection Line (except for last user) */}
                  {!isLast && (
                    <>
                      {isMobile ? (
                        <Box 
                          sx={{ 
                            height: 40, 
                            width: 2, 
                            bgcolor: 'primary.main',
                            opacity: 0.5,
                            mb: 1
                          }} 
                        />
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', mx: 1 }}>
                          <Box 
                            sx={{ 
                              height: 2, 
                              width: 30, 
                              bgcolor: 'primary.main',
                              opacity: 0.5
                            }} 
                          />
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              bgcolor: 'primary.main',
                              opacity: 0.6,
                              mx: 0.5
                            }}
                          />
                          <Box 
                            sx={{ 
                              height: 2, 
                              width: 30, 
                              bgcolor: 'primary.main',
                              opacity: 0.5
                            }} 
                          />
                        </Box>
                      )}
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="caption" color="text.secondary">
            Trust scores represent user credibility on a scale of 0-100. Higher scores indicate stronger trust relationships.
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TrustChain;